// src/components/froggerAI/wavEncoder.js
//
// THE VAD'S OWN AUDIO, NOT A PARALLEL RECORDING (I-03 R6 · S3-06.3).
//
// Conversation mode used to run a `MediaRecorder` alongside the detector
// and start it on `onSpeechStart`. That loses the beginning of every
// sentence: the detector only fires once it is CONFIDENT, which is
// 100-300 ms after the sound began, and by then the recorder has not
// been started. "Feed tank two" arrives as "...ank two" — the verb, the
// half of the sentence that decides what gets written, is the half that
// goes missing.
//
// `onSpeechEnd(audio)` hands back the segment the detector actually
// judged, INCLUDING its pre-speech padding (`preSpeechPadFrames`), as a
// Float32Array of mono samples at 16 kHz. That is the audio to send. It
// is not in any container, so this encodes it as WAV — which Whisper
// accepts, and which needs no codec, no MediaRecorder and no iOS/desktop
// branch at all.
//
// `pickMimeType` still exists and is still right, for the DESKTOP
// tap-to-record path, which has no detector and really is a
// MediaRecorder.

/** The sample rate `@ricky0123/vad-web` emits (Silero's own rate). */
export const VAD_SAMPLE_RATE = 16000;

/** 16-bit PCM: 1 channel, 2 bytes a sample. */
const BITS_PER_SAMPLE = 16;
const CHANNELS = 1;
const HEADER_BYTES = 44;

/**
 * Float samples in [-1, 1] -> a 16-bit PCM mono WAV Blob.
 *
 * CLAMPED BEFORE SCALING. A sample fractionally outside [-1, 1] — which
 * gain control produces routinely — wraps around in a DataView's Int16
 * write and becomes full-scale noise of the opposite sign: an audible
 * click on every loud syllable, and Whisper hears it too.
 */
export function encodeWavBuffer(samples, sampleRate = VAD_SAMPLE_RATE) {
  const input = samples || new Float32Array(0);
  const count = input.length;
  const dataBytes = count * (BITS_PER_SAMPLE / 8);
  const buffer = new ArrayBuffer(HEADER_BYTES + dataBytes);
  const view = new DataView(buffer);

  const ascii = (offset, text) => {
    for (let i = 0; i < text.length; i += 1) {
      view.setUint8(offset + i, text.charCodeAt(i));
    }
  };

  const byteRate = sampleRate * CHANNELS * (BITS_PER_SAMPLE / 8);

  ascii(0, 'RIFF');
  // RIFF chunk size = everything after this field.
  view.setUint32(4, 36 + dataBytes, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true);            // PCM fmt chunk size
  view.setUint16(20, 1, true);             // format = PCM
  view.setUint16(22, CHANNELS, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, CHANNELS * (BITS_PER_SAMPLE / 8), true);  // block align
  view.setUint16(34, BITS_PER_SAMPLE, true);
  ascii(36, 'data');
  view.setUint32(40, dataBytes, true);

  let offset = HEADER_BYTES;
  for (let i = 0; i < count; i += 1) {
    const s = Math.max(-1, Math.min(1, input[i]));
    // Asymmetric on purpose: -1 -> -32768, +1 -> 32767, which is what
    // 16-bit PCM actually is.
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  return buffer;
}

/**
 * The same bytes as a Blob, which is what the upload wants.
 *
 * Split from `encodeWavBuffer` because jsdom's Blob has no
 * `arrayBuffer()` — the header can only be ASSERTED on the buffer, and a
 * WAV encoder nobody can read back is a WAV encoder nobody has checked.
 */
export function encodeWav(samples, sampleRate = VAD_SAMPLE_RATE) {
  return new Blob([encodeWavBuffer(samples, sampleRate)], { type: 'audio/wav' });
}

/** How long a segment is, in seconds, for the turn's duration field. */
export const secondsOf = (samples, sampleRate = VAD_SAMPLE_RATE) => (
  (samples && samples.length ? samples.length : 0) / sampleRate);

export default encodeWav;
